/**
 * THE AUDIO HALF OF LIVE LISTEN-IN AND TAKEOVER, shared by the caller's page and
 * the panel console. Three pieces, and both ends use all three:
 *
 *   LiveTap      a MediaStream -> 20 ms live frames (PCM16, 16 kHz). The caller's
 *                page taps its mic and the agent's voice; the console taps the
 *                person's mic after a takeover.
 *   LivePlayer   live frames -> sound, one jitter buffer per voice
 *                (workers/live-player.js), with a mute per voice.
 *   AEC loopback where the player's sound goes: out of Web Audio, through two
 *                peer connections inside this page, into an <audio> element.
 *
 * WHY THE LOOPBACK. The browser's echo canceller only subtracts audio it knows
 * is being played, and it does not know about Web Audio output (the same fact
 * the header of ../greeting-clip.ts is built on). Played straight from Web
 * Audio, the person's voice would come out of the caller's speakers, go back
 * into the caller's mic, and be sent to the person as if the caller had said
 * it. Audio that arrives on a WebRTC track and plays through a media element is
 * what the canceller does hear, so the sound is sent through a WebRTC
 * connection to this same page first. If that connection cannot be made, the
 * player falls back to playing directly and says so in the console: an echo is
 * better than silence.
 *
 * NOTHING HERE CAN END A CALL. Every failure is logged and costs the live copy,
 * not the conversation — the rule the header of ../index.ts states for the
 * recorder applies to this too.
 */
import { Decimate48to16, LIVE_SAMPLE_RATE } from '@voiceai/wire'
import type { LiveSource } from '@voiceai/wire'
import tapSource from '../workers/pcm-tap.js?raw'
import playerSource from '../workers/live-player.js?raw'
import { makeOffer } from '../transport'
import { loadWorkletModule } from '../worklet-module'

export type LiveLog = (level: 'info' | 'warn' | 'error', message: string) => void

/** 20 ms at 48 kHz: one live frame after decimation to 16 kHz. */
const TAP_BLOCK_48K = 960
/** How much the player queues before it plays. About 100 ms, per docs/hitl.md §7. */
export const JITTER_TARGET_MS = 100
/** The most it ever queues. Past this the oldest audio is dropped. */
export const JITTER_MAX_MS = 300
/** How long the loopback may take to connect before playing directly instead. */
const LOOPBACK_CONNECT_MS = 3000
/** Host candidates only, so gathering is quick; this only bounds a stuck one. */
const LOOPBACK_GATHER_MS = 1000

/**
 * One MediaStream, tapped into live frames.
 *
 * Reuses workers/pcm-tap.js as it is: it posts 960-sample blocks, which at the
 * pinned 48 kHz is exactly 20 ms, and `Decimate48to16` turns each into one
 * 320-sample frame with nothing carried over.
 */
export class LiveTap {
  private readonly decimate = new Decimate48to16()
  private stopped = false

  private constructor(
    private readonly nodes: AudioNode[],
    private readonly port: MessagePort,
  ) {}

  /**
   * `silenced`, when it returns true, turns the frame into zeros. The caller's
   * page passes its mute and greeting hold here, so a muted caller is silent
   * in the copy for the same reason they are silent to the agent — and without
   * relying on a disabled track reaching Web Audio as silence, which is what
   * the spec says and not something to find out on a call.
   */
  static async open(
    ctx: AudioContext,
    stream: MediaStream,
    onFrame: (pcm: Int16Array) => void,
    silenced?: () => boolean,
  ): Promise<LiveTap> {
    if (ctx.sampleRate !== 48000) {
      throw new Error(`live audio needs a 48 kHz AudioContext, this one runs at ${ctx.sampleRate} Hz`)
    }
    await loadWorkletModule(ctx, tapSource)
    const src = ctx.createMediaStreamSource(stream)
    const node = new AudioWorkletNode(ctx, 'pcm-tap', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      processorOptions: { frameSamples: TAP_BLOCK_48K, track: 'live' },
    })
    // Through a zero gain to the destination, or the graph may never be pulled
    // and the tap never runs. See the header of workers/pcm-tap.js.
    const mute = ctx.createGain()
    mute.gain.value = 0
    src.connect(node)
    node.connect(mute)
    mute.connect(ctx.destination)
    const tap = new LiveTap([src, node, mute], node.port)
    node.port.onmessage = (ev: MessageEvent) => {
      const msg = ev.data as { t?: string; samples?: Float32Array }
      if (tap.stopped || msg?.t !== 'pcm' || !msg.samples) return
      const pcm = tap.decimate.process(msg.samples)
      if (pcm.length === 0) return
      onFrame(silenced?.() ? new Int16Array(pcm.length) : pcm)
    }
    return tap
  }

  stop(): void {
    if (this.stopped) return
    this.stopped = true
    this.port.onmessage = null
    for (const n of this.nodes) {
      try {
        n.disconnect()
      } catch {
        /* the context may already be closed */
      }
    }
  }
}

/** Where the player's sound goes, and which way it got there. */
export interface Playback {
  /** `loopback`: through a local WebRTC connection, heard by the echo
   *  canceller. `direct`: straight out of Web Audio, not heard by it. */
  path: 'loopback' | 'direct'
  /** The played sound as a stream, for a level meter. Null on `direct`. */
  stream: MediaStream | null
  close(): void
}

/**
 * Route `input` to the speakers through a local WebRTC loopback, or directly
 * when that fails. See the header for why.
 *
 * ONE PAIR OF PEER CONNECTIONS, BOTH IN THIS PAGE: `a` sends a
 * MediaStreamDestination's track, `b` receives it and plays it through an
 * <audio> element that is in the document. No ICE servers, because both ends
 * are here. The audio goes through an Opus encode and decode on the way, which
 * costs a few tens of milliseconds.
 */
export async function openAecLoopback(ctx: AudioContext, input: AudioNode, log: LiveLog): Promise<Playback> {
  let dest: MediaStreamAudioDestinationNode | null = null
  let a: RTCPeerConnection | null = null
  let b: RTCPeerConnection | null = null
  let el: HTMLAudioElement | null = null
  const closeLoopback = () => {
    for (const pc of [a, b]) {
      try {
        pc?.close()
      } catch {
        /* already closed */
      }
    }
    if (el) {
      el.srcObject = null
      el.remove()
    }
    try {
      if (dest) input.disconnect(dest)
    } catch {
      /* not connected */
    }
  }
  try {
    dest = ctx.createMediaStreamDestination()
    input.connect(dest)
    const track = dest.stream.getAudioTracks()[0]
    if (!track) throw new Error('the destination node has no audio track')
    const pa = new RTCPeerConnection()
    const pb = new RTCPeerConnection()
    a = pa
    b = pb
    const received = new Promise<MediaStream>((resolve) => {
      pb.ontrack = (ev) => resolve(ev.streams[0] ?? new MediaStream([ev.track]))
    })
    pa.addTrack(track, dest.stream)
    // NON-TRICKLE, the same way as the call's own offer (transport.ts): each
    // side waits for its host candidates and hands over one complete SDP.
    // Trickling here would race a candidate against the far side's remote
    // description, and host candidates take milliseconds to gather anyway.
    // `makeOffer` answers too: `setLocalDescription()` with no argument makes
    // whichever description the connection's state calls for.
    await pb.setRemoteDescription({ type: 'offer', sdp: await makeOffer(pa, LOOPBACK_GATHER_MS) })
    await pa.setRemoteDescription({ type: 'answer', sdp: await makeOffer(pb, LOOPBACK_GATHER_MS) })
    const stream = await withTimeout(received, LOOPBACK_CONNECT_MS, 'no track arrived')
    await withTimeout(connected(pb), LOOPBACK_CONNECT_MS, 'the connection did not come up')
    // In the document and not display:none, for the reason in transport.ts: a
    // detached element does not reliably play, and plays nothing on Safari.
    const audio = document.createElement('audio')
    el = audio
    audio.autoplay = true
    audio.setAttribute('aria-hidden', 'true')
    audio.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none'
    document.body.appendChild(audio)
    audio.srcObject = stream
    await audio.play()
    log('info', 'live audio plays through the local WebRTC loopback, so the echo canceller hears it')
    return { path: 'loopback', stream: dest.stream, close: closeLoopback }
  } catch (e) {
    closeLoopback()
    input.connect(ctx.destination)
    log('warn', `live audio plays directly, so the echo canceller may not hear it (the loopback failed: ${describe(e)})`)
    return {
      path: 'direct',
      stream: null,
      close: () => {
        try {
          input.disconnect()
        } catch {
          /* already disconnected */
        }
      },
    }
  }
}

/** Connected by either measure: `connectionState` is the newer one, and the
 *  ICE state is what an engine without it still reports. */
function connected(pc: RTCPeerConnection): Promise<void> {
  return new Promise((resolve, reject) => {
    const check = () => {
      const ice = pc.iceConnectionState
      if (pc.connectionState === 'connected' || ice === 'connected' || ice === 'completed') resolve()
      else if (pc.connectionState === 'failed' || pc.connectionState === 'closed' || ice === 'failed') {
        reject(new Error(`the loopback connection is ${pc.connectionState}`))
      }
    }
    pc.addEventListener('connectionstatechange', check)
    pc.addEventListener('iceconnectionstatechange', check)
    check()
  })
}

function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(what)), ms)
    p.then(
      (v) => {
        clearTimeout(timer)
        resolve(v)
      },
      (e: unknown) => {
        clearTimeout(timer)
        reject(e instanceof Error ? e : new Error(String(e)))
      },
    )
  })
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

interface Voice {
  node: AudioWorkletNode
  gain: GainNode
}

/**
 * Live frames to sound. One jitter buffer per voice, because the voices arrive
 * interleaved and independently late; a voice's node is made on its first
 * frame. Each voice has its own gain, which is the per-voice mute.
 *
 * THE JITTER BUFFER (workers/live-player.js, one per voice). Frames arrive
 * over a WebSocket in 20 ms pieces, and never evenly: TCP holds some back and
 * then delivers several at once. Playing each piece the moment it landed would
 * click at every late one, so the worklet keeps a short queue and plays from
 * it at the audio clock's steady rate. Three rules, and each one is a bound:
 *
 *   1. START LATE ON PURPOSE. Nothing plays until JITTER_TARGET_MS is queued,
 *      so the next late piece has something to cover for it. The same after
 *      the queue runs dry: silence, then wait for the target again.
 *   2. SILENCE WHEN EMPTY. An empty queue outputs zeros, never the last sample
 *      held or repeated.
 *   3. NEVER MORE THAN JITTER_MAX_MS. When a burst would overflow the queue,
 *      the OLDEST audio is dropped, down to the target. Late speech is worth
 *      less than current speech, and a queue allowed to grow turns one network
 *      stall into a delay the listener hears for the rest of the call.
 *
 * It resamples 16 kHz to the context's rate by linear interpolation: enough
 * for speech, and no filter state to get wrong when the queue runs dry. Its
 * comments are kept short because the file ships to every page as text.
 */
export class LivePlayer {
  private readonly voices = new Map<LiveSource, Voice>()
  private readonly muted = new Set<LiveSource>()
  private closed = false

  private constructor(
    private readonly ctx: AudioContext,
    private readonly bus: GainNode,
    private readonly out: Playback,
  ) {}

  static async open(ctx: AudioContext, log: LiveLog): Promise<LivePlayer> {
    await loadWorkletModule(ctx, playerSource)
    const bus = ctx.createGain()
    const out = await openAecLoopback(ctx, bus, log)
    return new LivePlayer(ctx, bus, out)
  }

  get path(): Playback['path'] {
    return this.out.path
  }

  /** What is being played, as a stream, when it goes through the loopback. */
  get stream(): MediaStream | null {
    return this.out.stream
  }

  push(source: LiveSource, pcm: Int16Array): void {
    if (this.closed || pcm.length === 0) return
    let voice = this.voices.get(source)
    if (!voice) {
      const node = new AudioWorkletNode(this.ctx, 'live-player', {
        numberOfInputs: 0,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        processorOptions: { inputRate: LIVE_SAMPLE_RATE, targetMs: JITTER_TARGET_MS, maxMs: JITTER_MAX_MS },
      })
      const gain = this.ctx.createGain()
      gain.gain.value = this.muted.has(source) ? 0 : 1
      node.connect(gain)
      gain.connect(this.bus)
      voice = { node, gain }
      this.voices.set(source, voice)
    }
    // Transferred, not copied: a decoded frame is a fresh buffer nobody else holds.
    voice.node.port.postMessage({ t: 'pcm', samples: pcm }, [pcm.buffer])
  }

  setMuted(source: LiveSource, muted: boolean): void {
    if (muted) this.muted.add(source)
    else this.muted.delete(source)
    const voice = this.voices.get(source)
    if (voice) voice.gain.gain.value = muted ? 0 : 1
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    for (const { node, gain } of this.voices.values()) {
      try {
        node.port.onmessage = null
        node.disconnect()
        gain.disconnect()
      } catch {
        /* the context may already be closed */
      }
    }
    this.voices.clear()
    this.out.close()
    try {
      this.bus.disconnect()
    } catch {
      /* already disconnected */
    }
  }
}
